/**
 * THÔNG BÁO ĐẨY trên thiết bị đang mở (03/10/2026, chủ dự án: «chuông báo về điện thoại») — phần tính
 * toán thuần. Việc xin quyền, đăng ký với trình duyệt và báo cho máy chủ nằm ở `stores/push-store.ts`.
 */
import { dueReminders, type Reminder } from '@halo/shared';

/**
 *  - `unsupported`   trình duyệt không có thông báo đẩy (trình duyệt nhúng trong Zalo, bản quá cũ, bản dev);
 *  - `needs-install` iPhone / iPad: chỉ ứng dụng ĐÃ CÀI lên màn hình chính mới nhận được thông báo;
 *  - `blocked`       thông báo của trang đang bị chặn — phải gỡ chặn trong cài đặt của trình duyệt;
 *  - `off`           bật được, chưa bật;
 *  - `on`            thiết bị này đang nhận thông báo.
 */
export type PushState = 'unsupported' | 'needs-install' | 'blocked' | 'off' | 'on';

export interface PushEnvironment {
  /** Có service worker đang quản trang (chỉ ở bản dựng thật). */
  hasWorker: boolean;
  /** Trình duyệt có `PushManager` và `Notification`. */
  hasPushApi: boolean;
  permission: 'default' | 'granted' | 'denied';
  /** Thiết bị đang có đăng ký với dịch vụ đẩy. */
  subscribed: boolean;
  ios: boolean;
  standalone: boolean;
}

export function pushState(environment: PushEnvironment): PushState {
  if (environment.ios && !environment.standalone) return 'needs-install';
  if (!environment.hasWorker || !environment.hasPushApi) return 'unsupported';
  if (environment.permission === 'denied') return 'blocked';
  return environment.permission === 'granted' && environment.subscribed ? 'on' : 'off';
}

/** Mục thông báo trong menu tài khoản; `null` = máy này không nhận được thông báo, khỏi bày mục. */
export type PushMenuAction = 'enable' | 'disable' | 'blocked' | null;

export function pushMenuAction(state: PushState): PushMenuAction {
  if (state === 'off') return 'enable';
  if (state === 'on') return 'disable';
  return state === 'blocked' ? 'blocked' : null;
}

/** Khoá công khai của máy chủ (base64url) → mảng byte cho `pushManager.subscribe`. */
export function applicationServerKey(publicKey: string): Uint8Array<ArrayBuffer> {
  const base64 = publicKey.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/**
 * Đăng ký đang có trên máy có đúng là với khoá hiện tại của máy chủ không. Khác khoá (máy chủ dựng lại
 * từ đầu) thì dịch vụ đẩy sẽ từ chối mọi tin — phải bỏ đăng ký cũ, đăng ký lại.
 */
export function sameServerKey(current: ArrayBuffer | null | undefined, publicKey: string): boolean {
  if (!current) return false;
  const expected = applicationServerKey(publicKey);
  const actual = new Uint8Array(current);
  return (
    actual.length === expected.length && actual.every((byte, index) => byte === expected[index])
  );
}

/**
 * NHẮC NGAY TRONG APP — cho máy CHƯA bật thông báo đẩy mà ứng dụng đang mở: lịch vừa tới giờ thì hiện
 * một thông báo trong trang. Hạn chờ ngắn hơn phía máy chủ: mở app lúc 9h thì không dội lại lời nhắc 8h30.
 */
export const IN_APP_GRACE_MINUTES = 5;

export function inAppReminders(
  reminders: readonly Reminder[],
  weekday: number,
  minutes: number,
  shownToday: ReadonlySet<string>,
): Reminder[] {
  return dueReminders(reminders, weekday, minutes, shownToday, IN_APP_GRACE_MINUTES);
}
